import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Message {
  id: number;
  from: 'user' | 'bot';
  text: string;
  showActions?: boolean;
}

// ─── Catálogo oficial San7o Pecado ───────────────────────────────────────────

const CHOCOLATE_SPECIALTIES = [
  'Bruce', 'Cebra', 'Chocorrol', 'Chocomenta',
  'Gansito', 'Kinder', 'Oreo', 'Trufa',
];

const TRES_LECHES = [
  'Tradicional', 'Fresas', 'Frutos Rojos', 'Mango',
  'Baileys', 'Conejito Vainilla', 'Conejito Chocolate',
];

const CLASICOS = [
  'Red Velvet', 'Zanahoria', 'Mil Hojas',
];

const GELATINAS = [
  'Gelatina 3 Chocolates', 'Gelatina Clericot', 'Gelatina de Mosaico', 'Gelatina de Rompope', 'Gelatina de Yogurth de Ale',
];

const HELADOS = ['Ferrero', 'Frutos Rojos'];

const FULL_MENU_RESPONSE = `Tenemos estos sabores disponibles 💜

🎂 **Especialidades de Chocolate:** ${CHOCOLATE_SPECIALTIES.join(', ')}

🧁 **Tres Leches:** ${TRES_LECHES.join(', ')}

🍰 **Clásicos y Especiales:** ${CLASICOS.join(', ')}

🍮 **Gelatinas:** ${GELATINAS.join(', ')}

🍦 **Helados** (solo 12–15 personas o individual): ${HELADOS.join(', ')}

🐶 También tenemos **pastel para perrito**

¿Para cuántas personas lo necesitas? 💜`;

const SIZES_RESPONSE = `Manejamos estos tamaños 📏

• **Individual** — para 1 persona
• **Mediano** — 4–5 personas
• **Grande** — 12–15 personas
• **Más personas** — sin límite (excepto gelatinas y helados)

⚠️ Las **gelatinas** solo se venden en tamaño 12–15 personas o vasitos individuales.
⚠️ Los pasteles **helados** solo se venden en tamaño 12–15 personas o individual.

¿Ya tienes en mente un sabor? 💜`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isHelado(text: string): boolean {
  return text.includes('helado');
}

function fuzzyMatchFlavor(input: string, flavors: string[]): string | null {
  const t = input.toLowerCase().trim();
  // Direct match: user typed the flavor name fully
  const direct = flavors.find(f => t.includes(f.toLowerCase()));
  if (direct) return direct;
  // Reverse match: flavor name contains the user's short input (e.g. "mosaico" → "Gelatina de Mosaico")
  const reverse = flavors.find(f => f.toLowerCase().includes(t));
  if (reverse) return reverse;
  return null;
}

function extractNumber(text: string): number | null {
  const words: Record<string, number> = {
    uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5,
    seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10,
    once: 11, doce: 12, quince: 15, veinte: 20,
    veinticinco: 25, treinta: 30, cuarenta: 40, cincuenta: 50,
  };
  for (const [word, val] of Object.entries(words)) {
    if (text.includes(word)) return val;
  }
  const match = text.match(/\b(\d{1,3})\b/);
  return match ? parseInt(match[1], 10) : null;
}

function extractPersonCount(text: string): number | null {
  const withWord = text.match(/(\d+)\s*(persona|gente|invitado|pax)/i);
  if (withWord) return parseInt(withWord[1], 10);
  return extractNumber(text);
}

function lastBotAskedPersons(history: Message[]): boolean {
  const botMsgs = history.filter(m => m.from === 'bot');
  if (botMsgs.length === 0) return false;
  const last = botMsgs[botMsgs.length - 1].text.toLowerCase();
  return (
    last.includes('cuántas personas') ||
    last.includes('para cuántas') ||
    last.includes('cuantas personas') ||
    last.includes('para cuantas')
  );
}

function getLastMentionedFlavor(history: Message[]): string | null {
  const recent = history.slice(-6);
  for (let i = recent.length - 1; i >= 0; i--) {
    const t = recent[i].text.toLowerCase();
    if (t.includes('helado') || fuzzyMatchFlavor(t, HELADOS)) return 'helado';
    if (t.includes('tres leches') || fuzzyMatchFlavor(t, TRES_LECHES)) return 'tres_leches';
    if (t.includes('chocolate') || fuzzyMatchFlavor(t, CHOCOLATE_SPECIALTIES)) return 'chocolate';
    if (t.includes('gelatina') || fuzzyMatchFlavor(t, GELATINAS)) return 'gelatina';
    if (fuzzyMatchFlavor(t, CLASICOS)) return 'clasico';
  }
  return null;
}

const isHeladoFlavor = (t: string) => fuzzyMatchFlavor(t, HELADOS) !== null;

// ─── Respuesta con acciones de cierre (WhatsApp + tienda) ────────────────────

function buildOrderReadyResponse(personCount: number, sizeLabel: string): { text: string; showActions: true } {
  return {
    text: `¡Perfecto! Para **${personCount} persona${personCount > 1 ? 's' : ''}** te recomendamos el tamaño **${sizeLabel}** 💜\n\n¿Listo para hacer tu pedido? Puedes escribirnos por WhatsApp o visitar nuestra tienda más cercana 🎂`,
    showActions: true,
  };
}

// ─── Lógica de respuesta con contexto ────────────────────────────────────────

function getBotResponse(input: string, history: Message[]): { text: string; showActions?: boolean } {
  const text = input.toLowerCase().trim();

  // ── Saludos ──
  if (['hola', 'hi', 'buenas', 'buenos', 'hey', 'buen día', 'buen dia'].some(g => text.includes(g))) {
    return { text: '¡Hola! 🎂 Soy El Confesionario, el asistente de San7o Pecado. Pregúntame sobre nuestros sabores y tipos de pasteles. ¿Qué se te antoja?' };
  }

  // ── Agradecimientos ──
  if (['gracias', 'ok', 'perfecto', 'listo', 'entendido', 'de acuerdo'].some(g => text.includes(g))) {
    return { text: '¡Con gusto! 😊 Si tienes más dudas sobre nuestros pasteles o sabores, aquí estoy 💜' };
  }

  // ── Contexto: el bot preguntó "¿para cuántas personas?" ──
  const personCount = extractPersonCount(text);
  const isJustANumber = /^\d+$/.test(text.trim()) || lastBotAskedPersons(history);

  if (personCount !== null && isJustANumber) {
    const lastFlavor = getLastMentionedFlavor(history);
    const isHeladoContext = lastFlavor === 'helado';
    const isGelatinaContext = lastFlavor === 'gelatina';

    if (isHeladoContext && personCount > 15) {
      return { text: 'Los pasteles helados solo se manejan en tamaño 12–15 personas o individual 💜 Si necesitas para más personas puedo recomendarte otro sabor. ¿Te interesa alguno de nuestros Clásicos o Tres Leches?' };
    }

    if (isGelatinaContext && personCount > 15) {
      return { text: 'Las gelatinas solo se venden en tamaño 12–15 personas o vasitos individuales 💜 Si necesitas para más personas puedo recomendarte otro tipo de postre. ¿Te interesa alguno de nuestros Tres Leches o Clásicos?' };
    }

    if (personCount === 1) {
      return buildOrderReadyResponse(1, 'Individual');
    }
    if (personCount <= 5) {
      return buildOrderReadyResponse(personCount, 'Mediano (4–5 personas)');
    }
    if (personCount <= 15) {
      return buildOrderReadyResponse(personCount, 'Grande (12–15 personas)');
    }
    if (isHeladoContext) {
      return { text: 'Los pasteles helados solo se manejan en tamaño 12–15 personas o individual 💜 Si necesitas para más personas puedo recomendarte otro sabor. ¿Te interesa alguno de nuestros Clásicos o Tres Leches?' };
    }
    if (isGelatinaContext) {
      return { text: 'Las gelatinas solo se venden en tamaño 12–15 personas o vasitos individuales 💜 Si necesitas para más personas puedo recomendarte otro tipo de postre. ¿Te interesa alguno de nuestros Tres Leches o Clásicos?' };
    }
    return buildOrderReadyResponse(personCount, `especial para +${personCount} personas`);
  }

  // ── Tamaño + sabor en el mismo mensaje ──
  if (personCount !== null) {
    const pedidoHelado = isHelado(text) || isHeladoFlavor(text);
    const pedidoGelatina = text.includes('gelatina') || fuzzyMatchFlavor(text, GELATINAS) !== null;

    if (pedidoHelado && personCount > 15) {
      return { text: 'Los pasteles helados solo se manejan en tamaño 12–15 personas o individual 💜 Si necesitas para más personas puedo recomendarte otro sabor. ¿Te interesa alguno de nuestros Clásicos o Tres Leches?' };
    }
    if (pedidoGelatina && personCount > 15) {
      return { text: 'Las gelatinas solo se venden en tamaño 12–15 personas o vasitos individuales 💜 Si necesitas para más personas puedo recomendarte otro tipo de postre. ¿Te interesa alguno de nuestros Tres Leches o Clásicos?' };
    }
    if (!pedidoHelado && !pedidoGelatina && personCount > 15) {
      return buildOrderReadyResponse(personCount, `especial para +${personCount} personas`);
    }
    if (personCount === 1) {
      return buildOrderReadyResponse(1, 'Individual');
    }
    if (personCount <= 5) {
      if (pedidoGelatina) {
        return { text: 'Las gelatinas solo se venden en tamaño 12–15 personas o vasitos individuales 💜 ¿Te sirve el tamaño de 12–15 o prefieres vasitos individuales?' };
      }
      return buildOrderReadyResponse(personCount, 'Mediano (4–5 personas)');
    }
    if (personCount <= 15) {
      return buildOrderReadyResponse(personCount, 'Grande (12–15 personas)');
    }
  }

  // ── Helados ──
  const heladoMatch = fuzzyMatchFlavor(text, HELADOS);
  if (isHelado(text) || heladoMatch) {
    if (heladoMatch) {
      return { text: `El pastel helado de **${heladoMatch}** es perfecto para el calor 🍦 ¿Para cuántas personas lo necesitas? Recuerda que solo manejamos tamaño 12–15 personas o individual 💜` };
    }
    return { text: `Nuestros **pasteles helados** están disponibles en estos sabores 🍦\n\n• Ferrero\n• Frutos Rojos\n\n⚠️ Solo se venden en tamaño **12–15 personas** o **individual**.\n\n¿Cuál te llama la atención? 💜` };
  }

  // ── Gelatinas ──
  const gelatinaMatch = fuzzyMatchFlavor(text, GELATINAS);
  if (text.includes('gelatina') || gelatinaMatch) {
    if (gelatinaMatch) {
      return { text: `La **${gelatinaMatch}** es una delicia 🍮 ¿Para cuántas personas la necesitas? Recuerda que solo manejamos tamaño 12–15 personas o vasitos individuales 💜` };
    }
    return { text: `Nuestras gelatinas están disponibles en estos sabores 🍮\n\n${GELATINAS.map(g => `• ${g}`).join('\n')}\n\n⚠️ Solo se venden en tamaño **12–15 personas** o **vasitos individuales**.\n\n¿Cuál te llama la atención? 💜` };
  }

  // ── Especialidades de chocolate ──
  if (
    text.includes('chocolate') ||
    text.includes('especialidad') ||
    CHOCOLATE_SPECIALTIES.some(s => text.includes(s.toLowerCase()))
  ) {
    const matched = CHOCOLATE_SPECIALTIES.filter(s => text.includes(s.toLowerCase()));
    if (matched.length > 0) {
      return { text: `El pastel **${matched[0]}** es una de nuestras especialidades de chocolate 🍫 ¡Está delicioso! ¿Para cuántas personas lo necesitas? 💜` };
    }
    return { text: `Nuestras **Especialidades de Chocolate** son 🍫\n\n${CHOCOLATE_SPECIALTIES.map(s => `• ${s}`).join('\n')}\n\n¿Cuál te llama la atención? 💜` };
  }

  // ── Tres Leches ──
  if (
    text.includes('tres leches') ||
    text.includes('tres-leches') ||
    TRES_LECHES.some(s => text.includes(s.toLowerCase()))
  ) {
    const matched = TRES_LECHES.filter(s => text.includes(s.toLowerCase()));
    if (matched.length > 0) {
      return { text: `El Tres Leches **${matched[0]}** es una opción increíble 🍓 ¿Para cuántas personas lo necesitas? 💜` };
    }
    return { text: `Nuestros **Tres Leches** son 🍓\n\n${TRES_LECHES.map(s => `• ${s}`).join('\n')}\n\n¿Cuál se te antoja? 💜` };
  }

  // ── Clásicos ──
  if (
    text.includes('clásico') || text.includes('clasico') ||
    CLASICOS.some(s => text.includes(s.toLowerCase()))
  ) {
    const matched = CLASICOS.filter(s => text.includes(s.toLowerCase()));
    if (matched.length > 0) {
      return { text: `El pastel **${matched[0]}** es uno de nuestros favoritos 🍰 ¿Para cuántas personas lo necesitas? 💜` };
    }
    return { text: `Nuestros **Clásicos y Especiales** son 🍰\n\n${CLASICOS.map(s => `• ${s}`).join('\n')}\n\n¿Cuál te gusta? 💜` };
  }


  // ── Pastel para perrito ──
  if (text.includes('perro') || text.includes('perrit') || text.includes('mascota')) {
    return { text: '¡Sí! 🐶 Tenemos **pastel para perrito**, hecho con ingredientes seguros y deliciosos para tu peludo. ¿Quieres más información? 💜' };
  }

  // ── Tamaños ──
  if (
    text.includes('tamaño') || text.includes('tamano') || text.includes('tamaños') ||
    text.includes('persona') || text.includes('cuántas') || text.includes('cuantas') ||
    text.includes('mediano') || text.includes('grande') || text.includes('individual')
  ) {
    return { text: SIZES_RESPONSE };
  }

  // ── Menú completo / sabores ──
  if (
    text.includes('sabor') || text.includes('todo') || text.includes('menú') ||
    text.includes('menu') || text.includes('catálogo') || text.includes('catalogo') ||
    text.includes('qué tienen') || text.includes('que tienen') || text.includes('tienen') ||
    text.includes('opciones') || text.includes('disponible')
  ) {
    return { text: FULL_MENU_RESPONSE };
  }

  // ── Pedido / encargo ──
  if (
    text.includes('pedido') || text.includes('encargar') || text.includes('encargo') ||
    text.includes('ordenar') || text.includes('pedir') || text.includes('comprar')
  ) {
    return { text: 'Para hacer tu pedido puedes escribirnos por WhatsApp 💜 Cuéntame primero: ¿qué sabor te interesa y para cuántas personas?', showActions: true };
  }

  // ── Precio ──
  if (
    text.includes('precio') || text.includes('cuánto') || text.includes('cuanto') ||
    text.includes('costo') || text.includes('vale') || text.includes('cuesta')
  ) {
    return { text: 'Para precios exactos te recomendamos contactarnos directamente, ya que varían según el tamaño y personalización 💜 ¿Quieres que te cuente más sobre algún sabor en específico?' };
  }

  // ── Fallback ──
  return { text: 'Solo puedo ayudarte con información sobre nuestros sabores y tipos de pasteles 🎂 Puedes preguntarme: "¿Qué sabores tienen?", "Pasteles de chocolate", "Tres Leches" o "¿Qué tamaños manejan?"' };
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 0,
      from: 'bot',
      text: '¡Hola! 🎂 Soy El Confesionario, el asistente de San7o Pecado. Pregúntame sobre nuestros sabores y tipos de pasteles.',
    },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = (text?: string) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;

    const userMsg: Message = { id: Date.now(), from: 'user', text: trimmed };

    setMessages(prev => {
      const updated = [...prev, userMsg];
      setInput('');
      setTyping(true);

      setTimeout(() => {
        const response = getBotResponse(trimmed, updated);
        setTyping(false);
        setMessages(prev2 => [
          ...prev2,
          {
            id: Date.now() + 1,
            from: 'bot',
            text: response.text,
            showActions: response.showActions,
          },
        ]);
      }, 700);

      return updated;
    });
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') sendMessage();
  };

  const quickReplies = [
    '¿Qué sabores tienen?',
    'Pasteles de chocolate',
    'Tres Leches',
    'Gelatinas',
  ];

  const renderText = (text: string) =>
    text.split('\n').map((line, i, arr) => {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <span key={i}>
          {parts.map((part, j) =>
            j % 2 === 1 ? <strong key={j}>{part}</strong> : part
          )}
          {i < arr.length - 1 && <br />}
        </span>
      );
    });

  const handleWhatsApp = () => {
    window.open('https://wa.me/5217291317070', '_blank');
  };

  const handleStore = () => {
    setOpen(false);
    navigate('/contacto');
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-110 cursor-pointer whitespace-nowrap overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0a2e, #3d1a6e)', boxShadow: '0 4px 24px rgba(90,30,140,0.45)' }}
        aria-label="Abrir El Confesionario"
      >
        {open
          ? <i className="ri-close-line text-white text-2xl" />
          : <img src="https://static.readdy.ai/image/707e9a4f9368999593f702b6caac5116/c192f8d7b7aa54799b917ce5af808e3e.png" alt="El Confesionario" className="w-full h-full object-cover rounded-full" />}
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-80 rounded-2xl overflow-hidden flex flex-col"
          style={{ height: '500px', boxShadow: '0 8px 32px rgba(0,180,200,0.18)' }}
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #00b4c8, #00c8a0)' }}
          >
            <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center">
              <img src="https://static.readdy.ai/image/707e9a4f9368999593f702b6caac5116/c192f8d7b7aa54799b917ce5af808e3e.png" alt="El Confesionario" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm leading-tight">El Confesionario</p>
              <p className="text-white/80 text-xs">Asistente de San7o Pecado</p>
            </div>
            <div className="ml-auto w-2 h-2 rounded-full bg-green-300" />
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" style={{ background: '#f8fffe' }}>
            {messages.map(msg => (
              <div key={msg.id} className={`flex flex-col ${msg.from === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`max-w-[88%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.from === 'user'
                      ? 'text-white rounded-br-sm'
                      : 'text-gray-700 rounded-bl-sm border border-[#e0f7fa]'
                  }`}
                  style={
                    msg.from === 'user'
                      ? { background: 'linear-gradient(135deg, #00b4c8, #00c8a0)' }
                      : { background: '#ffffff' }
                  }
                >
                  {renderText(msg.text)}
                </div>

                {/* Action buttons when order is ready */}
                {msg.showActions && msg.from === 'bot' && (
                  <div className="mt-2 flex flex-col gap-2 w-full max-w-[88%]">
                    <button
                      onClick={handleWhatsApp}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                      style={{ background: '#25D366' }}
                    >
                      <i className="ri-whatsapp-line text-base" />
                      Hacer pedido por WhatsApp
                    </button>
                    <button
                      onClick={handleStore}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors border border-[#00b4c8] text-[#00b4c8] hover:bg-[#e0f7fa]"
                      style={{ background: '#ffffff' }}
                    >
                      <i className="ri-map-pin-line text-base" />
                      Ver tienda más cercana
                    </button>
                  </div>
                )}
              </div>
            ))}

            {typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#e0f7fa] px-4 py-2 rounded-2xl rounded-bl-sm flex gap-1 items-center">
                  <span className="w-2 h-2 rounded-full bg-[#00b4c8] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#00b4c8] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#00b4c8] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick replies */}
          {messages.length <= 1 && (
            <div className="px-3 py-2 flex flex-wrap gap-1" style={{ background: '#f8fffe' }}>
              {quickReplies.map(q => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="text-xs px-3 py-1 rounded-full border border-[#00b4c8] text-[#00b4c8] hover:bg-[#e0f7fa] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-3 flex gap-2 border-t border-[#e0f7fa]" style={{ background: '#ffffff' }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Pregunta sobre sabores..."
              className="flex-1 text-sm px-3 py-2 rounded-full border border-[#b2ebf2] outline-none focus:border-[#00b4c8] bg-[#f8fffe] text-gray-700 placeholder-gray-400"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim()}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #00b4c8, #00c8a0)' }}
            >
              <i className="ri-send-plane-fill text-white text-sm" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
